import React, {memo, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {FmlxModal, FmlxLabel, FmlxTextBox} from 'fmlx-common-ui';
import withIntl from '../../lib/withIntl';
import {checkContainIllegalChars} from '../../lib/stringHelper';
import {ExplorerContentTypes} from '../../constants/dataEnum';

const CreateFolderModal = memo(
  ({intl, open, maxLength, protocolList, onCloseClick, onCancelClick, onCreateFolderClick}) => {
    const [folderName, setFolderName] = useState('');
    const [warning, setWarning] = useState('');

    const folderList = useMemo(
      () => protocolList.filter((p) => p.type === ExplorerContentTypes.FOLDER),
      [protocolList],
    );

    const checkMaxCharacterExceeded = (name) => name.length > maxLength;

    const checkFolderNameExist = (name) =>
      folderList.some((p) => p.name.trim().toLowerCase() === name.trim().toLowerCase());

    const validateValue = (newValue) => {
      if (!newValue?.trim()) {
        setWarning(intl.formatMessage({id: 'explorer.modal.rename.warning.required'}));
        return;
      }

      if (checkContainIllegalChars(newValue)) {
        setWarning(intl.formatMessage({id: 'explorer.modal.rename.warning.illegalCharacters'}));
        return;
      }

      if (checkMaxCharacterExceeded(newValue)) {
        setWarning(intl.formatMessage({id: 'explorer.modal.rename.warning.modalMaxCharacters'}));
        return;
      }

      if (checkFolderNameExist(newValue)) {
        setWarning(
          intl.formatMessage(
            {id: 'explorer.modal.rename.warning.nameAlreadyExists'},
            {
              type: 'Folder',
            },
          ),
        );
        return;
      }

      setWarning('');
    };

    function resetFolderNameValue() {
      setFolderName('');
    }

    function handleChangeTextBox(input) {
      const {value} = input;
      setFolderName(value);
      validateValue(value);
    }

    function handleCreateFolderClick() {
      if (warning || !folderName) return;

      onCreateFolderClick(folderName);
      resetFolderNameValue();
    }

    return (
      <FmlxModal
        open={open}
        title={intl.formatMessage({id: 'explorer.explorerHeader.createFolderModal.title'})}
        onCloseClick={onCloseClick}
        tertiaryButton={{show: false}}
        primaryButton={{
          label: intl.formatMessage({id: 'explorer.explorerHeader.createFolderModal.create'}),
          disabled: !!warning || !folderName,
          show: true,
          onClick: handleCreateFolderClick,
        }}
        secondaryButton={{
          label: intl.formatMessage({id: 'explorer.explorerHeader.createFolderModal.cancel'}),
          disabled: false,
          show: true,
          type: 'basic',
          variant: 'outline',
          onClick: onCancelClick,
        }}
      >
        <div className="name-text-box">
          <FmlxLabel label={intl.formatMessage({id: 'explorer.explorerHeader.createFolderModal.label'})} />
          <FmlxTextBox
            autoFocus
            value={folderName}
            onChange={handleChangeTextBox}
            maxLength={maxLength}
            inlineText={warning}
            error={!!warning}
            decoration="counter"
            onEnterKeyPressed={handleCreateFolderClick}
          />
        </div>
      </FmlxModal>
    );
  },
);

CreateFolderModal.propTypes = {
  intl: PropTypes.any,
  open: PropTypes.bool.isRequired,
  maxLength: PropTypes.number,
  protocolList: PropTypes.array,
  onCloseClick: PropTypes.func.isRequired,
  onCancelClick: PropTypes.func.isRequired,
  onCreateFolderClick: PropTypes.func.isRequired,
};

CreateFolderModal.defaultProps = {
  intl: {},
  maxLength: 255,
  protocolList: [],
};

export default withIntl(CreateFolderModal);
